import { api } from './api.js';

/**
 * 405 (#315, specs/405-cartrack-live-position/contracts/api.md): the Cartrack routes the admin reads. Types follow
 * the backend's responses; the panel decides nothing the platform has not already decided.
 */
export type MatchOutcome =
  | 'link'
  | 'already_linked'
  | 'manual_differs'
  | 'not_on_cartrack'
  | 'vehicle_without_car'
  | 'conflict'
  | 'vehicle_gone'
  | 'linked_car_inactive';

export interface MatchRow {
  outcome: MatchOutcome;
  car: { id: string; plate_number: string } | null;
  vehicle: { vehicle_id: number; registration: string | null } | null;
  detail: 'duplicate_plate' | 'duplicate_registration' | 'vehicle_linked_to_other_car' | null;
}

export interface MatchPlan {
  rows: MatchRow[];
  summary: Record<MatchOutcome, number>;
  plan_sha: string;
}

export interface CartrackVehicle {
  vehicle_id: number;
  registration: string | null;
  vehicle_name: string | null;
  make_model: string | null;
  car: { id: string; plate_number: string } | null;
}

export interface CartrackLinkChange {
  from_vehicle_id: number | null;
  to_vehicle_id: number | null;
  cause: 'match' | 'manual';
  registration: string | null;
  at: string;
  by: { id: string; name: string };
}

export type CarPosition =
  | {
      state: 'live';
      latitude: number;
      longitude: number;
      fix_at: string | null;
      stale: boolean;
      ignition: boolean | null;
      speed_kmh: number | null;
      odometer_km: number | null;
      /** 416 (#1431): the car's location zone ([lng, lat] corners) and whether the car is inside it. */
      zone?: { polygon: [number, number][]; inside: boolean | null } | null;
    }
  | { state: 'unlinked' }
  | { state: 'unavailable'; reason: string };

/** 419 (#1529, contracts/immobiliser.md): the car's immobiliser, as `GET /cars/:id/immobiliser` answers it. */
export type ImmobiliserAction = 'immobilise' | 'release';
export interface ImmobiliserCommand {
  id: string;
  action: ImmobiliserAction;
  status: 'sent' | 'confirmed' | 'unconfirmed' | 'refused';
  requested_at: string;
  requested_by_name: string;
  confirmed_at: string | null;
  cartrack_code: string | null;
  /** Only for staff who may command the immobiliser. */
  reason?: string;
}
export interface CarImmobiliser {
  configured: boolean;
  available: boolean;
  state: 'released' | 'immobilised' | 'unknown' | 'not_available';
  waiting: { action: ImmobiliserAction; requested_at: string } | null;
  unconfirmed: boolean;
  engine: 'on' | 'off' | 'unknown';
  can_command: boolean;
  commands: ImmobiliserCommand[];
  /** 420: the doors (absent on an older backend, so read by shape). */
  door?: CarDoor;
}

/** 420 (contracts/fleet-lock.md): door lock / unlock through Cartrack; there is no lock state to read. */
export type DoorCommandWord = 'lock' | 'unlock';
export interface DoorCommandRow {
  id: string;
  command: DoorCommandWord;
  status: 'sent' | 'refused';
  requested_at: string;
  requested_by_name: string;
  cartrack_code: string | null;
  retry_after_s: number | null;
  /** Only for staff who may lock and unlock doors. */
  reason?: string;
}
export interface CarDoor {
  configured: boolean;
  can_command: boolean;
  commands: DoorCommandRow[];
}

/** 420 (contracts/fleet-lock.md): every Cartrack vehicle's immobiliser state. */
export type FleetImmobiliserState = 'immobilised' | 'released' | 'unknown' | 'not_available';
export interface FleetImmobiliserRow {
  vehicle_id: number;
  registration: string | null;
  make_model: string | null;
  state: FleetImmobiliserState;
  car: { id: string; plate_number: string } | null;
  waiting: { action: ImmobiliserAction; requested_at: string } | null;
}
export interface FleetImmobilisers {
  read_via: 'listing' | 'per_vehicle';
  fetched_at: string;
  items: FleetImmobiliserRow[];
  total: number;
  counts: Record<'all' | FleetImmobiliserState, number>;
}

export const CARTRACK_KEYS = {
  match: ['cartrack', 'match'] as const,
  immobilisers: ['cartrack', 'immobilisers'] as const,
  vehicles: ['cartrack', 'vehicles'] as const,
  changes: (carId: string) => ['car', carId, 'cartrack-changes'] as const,
  position: (carId: string) => ['car', carId, 'position'] as const,
  immobiliser: (carId: string) => ['car', carId, 'immobiliser'] as const,
};

export const cartrackApi = {
  match: () => api.get<MatchPlan>('/cartrack/match'),
  applyMatch: (expectSha: string) =>
    api.post<{ linked: number; skipped: number }>('/cartrack/match', { expect_sha: expectSha }),
  vehicles: () => api.get<{ items: CartrackVehicle[]; total: number }>('/cartrack/vehicles'),
  immobilisers: () => api.get<FleetImmobilisers>('/cartrack/immobilisers'),
  setCarVehicle: (carId: string, vehicleId: number | null) =>
    api.put<{ cartrack_vehicle_id: number | null; cartrack_linked_at: string | null }>(
      `/cars/${carId}/cartrack`,
      { vehicle_id: vehicleId },
    ),
  changes: (carId: string) =>
    api.get<{ items: CartrackLinkChange[] }>(`/cars/${carId}/cartrack-changes`),
  position: (carId: string) => api.get<CarPosition>(`/cars/${carId}/position`),
  immobiliser: (carId: string) => api.get<CarImmobiliser>(`/cars/${carId}/immobiliser`),
  commandImmobiliser: (
    carId: string,
    body: { action: ImmobiliserAction; reason: string; warranty_agreed?: true },
  ) => api.post<{ command: { id: string } }>(`/cars/${carId}/immobiliser`, body),
  commandDoors: (carId: string, body: { command: DoorCommandWord; reason: string }) =>
    api.post<{ command: { id: string; command: DoorCommandWord; status: 'sent' } }>(
      `/cars/${carId}/doors`,
      body,
    ),
};

const REASONS: Record<string, string> = {
  auth: 'login refused',
  rate_limited: 'too many requests',
  timeout: 'no answer in time',
  network: 'no connection',
  malformed: 'an unreadable answer',
};

/**
 * A refusal's status, code and details, read by shape rather than `instanceof ApiError`: many page suites stand in
 * `services/api.js` without the class, and every car-page card must load under them.
 */
export function refusal(
  err: unknown,
): { status: number; code: string; details: Record<string, unknown> } | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { status?: unknown; code?: unknown; details?: unknown };
  if (typeof e.status !== 'number') return null;
  return {
    status: e.status,
    code: typeof e.code === 'string' ? e.code : 'UNKNOWN',
    details:
      e.details && typeof e.details === 'object' ? (e.details as Record<string, unknown>) : {},
  };
}

/** A refusal from a Cartrack route, in words; null when it is not one of Cartrack's own. */
export function cartrackProblem(error: unknown): { tone: 'warn' | 'error'; text: string } | null {
  const err = refusal(error);
  if (!err) return null;
  if (err.code === 'CARTRACK_NOT_CONFIGURED') {
    return { tone: 'warn', text: 'Cartrack is not set up for this environment.' };
  }
  if (err.code === 'CARTRACK_UNAVAILABLE') {
    const reason = REASONS[String(err.details.reason)] ?? 'not answering';
    return { tone: 'error', text: `Cartrack could not be reached (${reason}). Try again later.` };
  }
  return null;
}
